(ns rems.administration.license
  (:require [clojure.string :as str]
            [re-frame.core :as rf]
            [rems.administration.administration :as administration]
            [rems.administration.components :refer [inline-info-field localized-info-field perform-action-button]]
            [rems.administration.status-flags :as status-flags]
            [rems.atoms :as atoms :refer [license-attachment-link external-link readonly-checkbox document-title]]
            [rems.common.util :refer [andstr]]
            [rems.config :as config]
            [rems.collapsible :as collapsible]
            [rems.flash-message :as flash-message]
            [rems.common.roles :as roles]
            [rems.spinner :as spinner]
            [rems.text :refer [get-localized-title localized text text-format]]
            [rems.util :refer [fetch]]))

(rf/reg-event-fx
 ::enter-page
 (fn [{:keys [db]} [_ license-id]]
   {:db (assoc db ::loading? true)
    ::fetch-license [license-id]}))

(defn- fetch-license [license-id]
  (fetch (str "/api/licenses/" license-id)
         {:handler #(rf/dispatch [::fetch-license-result %])
          :error-handler (flash-message/default-error-handler :top "Fetch license")}))

(rf/reg-fx ::fetch-license (fn [[license-id]] (fetch-license license-id)))

(rf/reg-event-db
 ::fetch-license-result
 (fn [db [_ license]]
   (-> db
       (assoc ::license license)
       (dissoc ::loading?))))

(rf/reg-sub ::license (fn [db _] (::license db)))
(rf/reg-sub ::loading? (fn [db _] (::loading? db)))

(defn- toggle-enabled [license]
  (status-flags/enabled-toggle-action
   {:on-change #(rf/dispatch [:rems.administration.licenses/set-license-enabled %1 %2 [::enter-page (:id license)]])}
   license))

(defn- toggle-archived [license]
  (status-flags/archived-toggle-action
   {:on-change #(rf/dispatch [:rems.administration.licenses/set-license-archived %1 %2 [::enter-page (:id license)]])}
   license))

(defn- license-view [license]
  [:div.spaced-vertically-3
   [collapsible/component
    {:id "license"
     :title [:span (andstr (localized (get-in license [:organization :organization/short-name])) "/") (get-localized-title license)]
     :always [:div#license
              [inline-info-field (text :t.administration/organization) (localized (get-in license [:organization :organization/name]))]
              [localized-info-field license {:localizations-key :title
                                             :label (text :t.administration/title)}]
              [inline-info-field (text :t.administration/type) (:licensetype license)]
              (case (:licensetype license)
                "link"
                (into [:<>]
                      (for [langcode @rems.config/languages
                            :let [textcontent (get-in license [:localizations langcode :textcontent])]]
                        [inline-info-field
                         (str (text :t.create-license/external-link)
                              " (" (str/upper-case (name langcode)) ")")
                         [:a {:target :_blank :href textcontent}
                          textcontent " " [external-link]]]))
                "text"
                [localized-info-field license {:localizations-key :textcontent
                                               :label (text :t.create-license/license-text)}]
                "attachment"
                (into [:<>]
                      (for [langcode @rems.config/languages
                            :let [attachment-id (get-in license [:localizations langcode :attachment-id])
                                  title (get-in license [:localizations langcode :title])]]
                        [inline-info-field
                         (str (text :t.create-license/license-attachment)
                              " (" (str/upper-case (name langcode)) ")")
                         (when (some? attachment-id)
                           [license-attachment-link attachment-id title])
                         {:box? false}]))
                nil)
              [inline-info-field (text :t.administration/active)
               [readonly-checkbox {:value (status-flags/active? license)}]]]}]

   [:div.col.commands
    [administration/back-button "/administration/licenses"]
    [roles/show-when roles/+admin-write-roles+
     [perform-action-button (toggle-enabled license)]
     [perform-action-button (toggle-archived license)]]]])

;; XXX: Duplicates much of license-view. One notable difference is that
;;      here the license text is only shown in the current language.
(defn- license-view-compact [license]
  (into [:div.form-item
         [:h3.license-title (text-format :t.administration/license-field (get-localized-title license))]]
        (concat (for [[langcode localization] (:localizations license)]
                  [inline-info-field
                   (str (text :t.administration/title)
                        " (" (str/upper-case (name langcode)) ")")
                   (:title localization)])
                [[inline-info-field (text :t.administration/type) (:licensetype license)]]
                (when (= "link" (:licensetype license))
                  (for [[langcode localization] (:localizations license)]
                    [inline-info-field
                     (str (text :t.create-license/external-link)
                          " (" (str/upper-case (name langcode)) ")")
                     [:a {:target :_blank :href (:textcontent localization)}
                      (:textcontent localization) " " [external-link]]]))
                (when (= "text" (:licensetype license))
                  (let [localization (localized (:localizations license))]
                    (when (:textcontent localization)
                      [[inline-info-field (text :t.create-license/license-text)
                        (:textcontent localization)]])))
                (when (= "attachment" (:licensetype license))
                  (for [[langcode localization] (:localizations license)]
                    (when (:attachment-id localization)
                      [inline-info-field
                       (str (text :t.create-license/license-attachment)
                            " (" (str/upper-case (name langcode)) ")")
                       [license-attachment-link (:attachment-id localization) (:title localization)]
                       {:box? false}]))))))

(defn licenses-view [licenses]
  [collapsible/component
   {:id "licenses"
    :title (text :t.administration/licenses)
    :top-less-button? (> (count licenses) 5)
    :bottom-less-button? (pos? (count licenses))
    :open? (<= (count licenses) 5)
    :collapse (if (seq licenses)
                (into [:div]
                      (for [license (->> licenses
                                         (mapv #(assoc % ::title (get-localized-title %)))
                                         (sort-by ::title))]
                        [license-view-compact license]))
                [:p (text :t.administration/no-licenses)])}])

(defn license-page []
  (let [license (rf/subscribe [::license])
        loading? (rf/subscribe [::loading?])]
    (fn []
      [:div
       [administration/navigator]
       [document-title (text :t.administration/license)]
       [flash-message/component :top]
       (if @loading?
         [spinner/big]
         [license-view @license])])))
